import React, { useState, useEffect } from "react";
import Modal from "../../common/components/Modal";
import { OriginalPost } from "./OriginalPostTable";
import OriginalPostViewMode from "./OriginalPostViewMode";
import OriginalPostEditMode from "./OriginalPostEditMode";
import { useOmniOriginalPostDetailQuery, OmniOriginalPostDetailQuery } from "../../../lib/apollo/graphql/omni/omni-generated";

type OriginalPostDetail = OmniOriginalPostDetailQuery['originalPost'];

interface Props {
  post: OriginalPost | null;
  open: boolean;
  onClose: () => void;
  onSave?: (post: OriginalPost) => void;
  onDelete?: (postId: string) => void;
  onSchedulePublish?: (postId: string) => void;
}


const OriginalPostDetailModal: React.FC<Props> = React.memo(({
  post,
  open,
  onClose,
  onSave,
  onDelete,
  onSchedulePublish,
}) => {
  const [currentStatus, setCurrentStatus] = useState(post?.status || "NEW");
  const [detailData, setDetailData] = useState<OriginalPostDetail | null>(null);
  
  // 상세 정보 조회 쿼리 (post와 open이 모두 있을 때만 실행)
  const { data: detailQueryData, loading: detailLoading, error: detailError, refetch: refetchDetail } = useOmniOriginalPostDetailQuery({
    variables: {
      originalPostId: post?.id || "",
    },
    skip: !post || !open || !post.id, // post나 open이 없거나 post.id가 없으면 쿼리 스킵
  });
  
  
  // post가 변경될 때마다 currentStatus 업데이트 및 상세 데이터 새로고침
  useEffect(() => {
    if (post) {
      setCurrentStatus(post.status);
      // 모달이 열리고 post가 변경되면 상세 데이터 새로고침
      if (open && post.id && post.id.trim() !== '') {
        refetchDetail();
      }
    }
  }, [post, open, refetchDetail]);
  
  // 상세 데이터 업데이트
  useEffect(() => {
    if (detailQueryData?.originalPost) {
      setDetailData(detailQueryData.originalPost);
    }
  }, [detailQueryData]);
  
  // 에러 처리
  useEffect(() => {
    if (detailError) {
      console.error('Failed to fetch original post detail:', detailError);
    }
  }, [detailError]);
  
  if (!post || !open) return null;

  // 에러가 있고 상세 데이터가 없는 경우 에러 표시
  if (detailError && !detailData) {
    return (
      <Modal open={open} onClose={onClose} width="90%" height="90%">
        <div className="flex items-center justify-center p-8">
          <div className="text-center">
            <div className="text-red-500 mb-2">상세 정보를 불러오는 중 오류가 발생했습니다.</div>
            <div className="text-sm text-gray-600 mb-4">{detailError.message}</div>
            <button
              onClick={() => refetchDetail()}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              다시 시도
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  // 로딩 중인 경우
  if (detailLoading && !detailData) {
    return (
      <Modal open={open} onClose={onClose} width="90%" height="90%">
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <span className="ml-2">상세 정보를 불러오는 중...</span>
        </div>
      </Modal>
    );
  }
  
  // 상세 데이터가 있으면 사용, 없으면 기본 post 데이터 사용
  const displayPost = detailData ? {
    ...post,
    content: detailData.content || "",
    memo: detailData.memo || "",
    sourceUrl: detailData.sourceUrl,
    tags: detailData.tags,
    title: detailData.title,
    status: detailData.status,
    createdAt: detailData.createdAt,
    updatedAt: detailData.updatedAt,
    updatedBy: detailData.updatedBy,
    publishedAt: detailData.publishedAt,
  } : post;

  // 디버깅용 로그


  const getModalComponent = () => {
    // currentStatus를 사용하여 현재 선택된 상태로 컴포넌트 결정
    
    switch (currentStatus) {
      case "NEW":
      case "SELECTED":
      case "ON_HOLD":
      case "DRAFT_REQUEST_FAILED":
        return (
          <OriginalPostEditMode
            post={displayPost}
            onSave={onSave}
            onDelete={onDelete}
            onClose={onClose}
            onRefreshDetail={refetchDetail}
            onSchedulePublish={onSchedulePublish}
          />
        );
      
      case "DRAFTED":
      case "DRAFT_REQUESTED":
      default:
        return (
          <OriginalPostViewMode
            post={displayPost}
            onSave={onSave}
            onClose={onClose}
            onRefreshDetail={refetchDetail}
            onSchedulePublish={onSchedulePublish}
          />
        );
    }
  };


  return (
    <Modal
      open={open}
      onClose={onClose}
      title="원문 상세"
      width="90%"
      height="90%"
    >
      <div className="flex h-full w-full flex-col bg-white">
        {/* 컨텐츠 영역 */}
        <div className="flex-1 overflow-auto">
          {getModalComponent()}
        </div>
      </div>
    </Modal>
  );
});

OriginalPostDetailModal.displayName = 'OriginalPostDetailModal';

export default OriginalPostDetailModal;